import { useState } from "react";
import type { Money, WorkOrderLaborLine } from "@autorepair/contracts";
import { parseMoneyInput } from "../lib/money";
import {
  ModernButton,
  ModernCheckboxRow,
  ModernConfirmDialog,
  ModernErrorBanner,
  ModernField,
  ModernInput,
  ModernModal,
  ModernTextarea,
} from "./ui-modern";
import { useConfirmGate } from "../lib/useConfirm";
import { useShopTypeLabels } from "../lib/theme";

export function EditLaborLineModal({
  line,
  // Current shop laborTaxRateSnapshot — see EditPartLineModal.tsx's own
  // comment on this exact pattern.
  taxRate = "0",
  onSave,
  onClose,
}: {
  line: WorkOrderLaborLine;
  taxRate?: string;
  onSave: (values: { description: string; extendedAmount: Money; isTaxable?: boolean }) => Promise<void>;
  onClose: () => void;
}) {
  const [description, setDescription] = useState(line.description);
  const [amount, setAmount] = useState<string>(line.extendedAmount);
  const [taxExempt, setTaxExempt] = useState(!line.isTaxable);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const confirmGate = useConfirmGate();
  const shopTypeLabels = useShopTypeLabels();
  const showTaxExempt = Number(taxRate) > 0;

  async function save(): Promise<void> {
    const extendedAmount = parseMoneyInput(amount);
    if (!extendedAmount) return setError('Amount should look like "265.50".');
    setError(null);
    setSaving(true);
    try {
      await onSave({ description, extendedAmount, isTaxable: showTaxExempt ? !taxExempt : undefined });
      onClose();
    } catch {
      setError("Could not save this labor line. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    confirmGate.request(() => void save());
  }

  return (
    <ModernModal title={`Edit ${shopTypeLabels.laborLabel}`} onCancel={onClose}>
      {error && <ModernErrorBanner message={error} />}
      <form onSubmit={handleSubmit}>
        <ModernField label="What Was Done" htmlFor="editLaborDescription" required>
          <ModernTextarea id="editLaborDescription" required uppercase rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </ModernField>
        <ModernField label="Amount" htmlFor="editLaborAmount">
          <ModernInput id="editLaborAmount" decimal value={amount} onChange={(e) => setAmount(e.target.value)} />
          {showTaxExempt && (
            <ModernCheckboxRow className="mt-1.5" checked={taxExempt} onChange={setTaxExempt}>
              Tax exempt
            </ModernCheckboxRow>
          )}
        </ModernField>
        <div className="flex gap-2.5">
          <ModernButton type="submit" disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </ModernButton>
          <ModernButton type="button" variant="secondary" onClick={onClose}>
            Cancel
          </ModernButton>
        </div>
      </form>
      <ModernConfirmDialog {...confirmGate.dialogProps} message={`Save these changes to the ${shopTypeLabels.laborLabel.toLowerCase()} line?`} />
    </ModernModal>
  );
}
